import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import { Card, Chip, Skeleton, Typography } from "@mui/material";

import DelayedLink from "@common/DelayedLink";
import AnimateOnClick from "@common/animations/AnimateOnClick";
import TimeRemainingPie from "./TimeRemainingPie";

import useShopContext from "@hooks/useShopContext";
import ProductPricingContainer from "@common/product/ProductPricingContainer";
import { prefixCurrency } from "@common/utils/currency";
import { hasVariantOnDiscount } from "@common/utils/productListUtils";
import { getPercentageDiscount } from "@common/utils/productUtils";
import Mascot from "@common/seasonal/Mascot";

const PREFIX = "ProductCard";

const classes = {
  discountLabel: `${PREFIX}-discountLabel`,
  imageContainer: `${PREFIX}-imageContainer`,
  image: `${PREFIX}-image`,
  imageSkeleton: `${PREFIX}-imageSkeleton`,
  timeBasedLabel: `${PREFIX}-timeBasedLabel`,
  soldOutLabel: `${PREFIX}-soldOutLabel`,
};

const StyledCard = styled(Card)(({ theme, soldout }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  width: "250px",
  padding: theme.spacing(1.5),
  border: theme.bordered ? `2px solid ${theme.palette.secondary.main}` : "none",
  boxShadow: theme.bordered ? "none" : theme.shadows[3],
  borderRadius: 5,
  textAlign: "left",
  height: "100%",
  position: "relative",
  "&:hover": {
    cursor: "pointer",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
  zIndex: 10,

  [`& .${classes.discountLabel}`]: {
    height: "16px",
    marginBottom: "2px",
    // backgroundColor: "red",
    // color: "#fff",
    fontSize: "0.75rem",
    "& .MuiChip-label": {
      padding: "0px 8px",
    },
  },

  [`& .${classes.imageContainer}`]: {
    // width: "100%",
    // opacity: soldout ? 0.4 : 1,
    // aspectRatio: "1/1", // not support in few outdated browsers
    // overflow: "hidden",
    width: "100%",
    opacity: soldout ? 0.4 : 1,
    height: 0,
    paddingTop: "100%",
    objectFit: "contain",
    position: "relative",
  },

  [`& .${classes.image}`]: {
    position: "absolute",
    top: "0",
    objectFit: "cover",
    width: "100%",
    height: "100%",
  },

  [`& .${classes.imageSkeleton}`]: {
    position: "absolute",
    top: "0",
    width: "100%",
    height: "100%",
  },

  [`& .${classes.timeBasedLabel}`]: {
    position: "absolute",
    top: theme.spacing(2),
    right: theme.spacing(2),
  },

  [`& .${classes.soldOutLabel}`]: {
    position: "absolute",
    right: 0,
    bottom: theme.spacing(3),
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    padding: theme.spacing(0.5, 1),
  },
}));

const ProductCard = ({ renderSprite, product }) => {
  const { shop, vendor } = useShopContext();

  const soldout = product && product.sold_out;

  const [imageLoaded, setImageLoaded] = useState(false);

  const getLowestPrice = () => {
    const minPrice =
      product.variants &&
      product.variants.length > 0 &&
      product.variants.reduce(
        (a, b) => ({ price: Math.min(parseFloat(a.price), parseFloat(b.price)) }),
        {
          price: Number.MAX_VALUE,
        },
      );
    return minPrice ? parseFloat(minPrice.price).toFixed(2) : "";
  };

  const showPricing = () => {
    if (!product) {
      return "";
    }

    if (product.original_price) {
      return (
        <ProductPricingContainer sx={{ mb: 1 }}>
          <Typography variant="body2">
            <span>{prefixCurrency(product.price, vendor)}</span>
          </Typography>
          <Typography variant="body2">
            <span style={{ textDecoration: "line-through", fontSize: "0.85rem" }}>
              {prefixCurrency(product.original_price, vendor)}
            </span>
          </Typography>
          {product.show_percentage_discount && (
            <Chip
              label={getPercentageDiscount(product.original_price, product.price) + `%`}
              className={classes.discountLabel}
            />
          )}
        </ProductPricingContainer>
      );
    }

    if (product.use_variants && hasVariantOnDiscount(product)) {
      return (
        <ProductPricingContainer sx={{ mb: 1 }}>
          <Typography variant="body2">
            From {prefixCurrency(getLowestPrice(product), vendor)}
          </Typography>
          <Chip label={`Sale`} className={classes.discountLabel} />
        </ProductPricingContainer>
      );
    }

    if (product.use_variants) {
      return (
        <ProductPricingContainer sx={{ mb: 1 }}>
          <Typography variant="body2">{prefixCurrency(getLowestPrice(product), vendor)}</Typography>
        </ProductPricingContainer>
      );
    }

    return (
      <ProductPricingContainer sx={{ mb: 1 }}>
        <Typography variant="body2">{prefixCurrency(product.price, vendor)}</Typography>
      </ProductPricingContainer>
    );
  };

  return (
    <DelayedLink href={`/products/${product && product.product_id}`} disableRipple>
      <AnimateOnClick style={{ flex: 1, height: "100%", position: "relative" }}>
        {renderSprite && (
          <Mascot
            enabled={shop?.shop_settings?.enable_seasonal_features}
            type={shop?.shop_settings?.mascot || "dragon"}
          />
        )}
        <StyledCard elevation={0} soldout={soldout ? "true" : undefined}>
          {product && product.available_start_time && product.available_end_time && (
            <div className={classes.timeBasedLabel}>
              <TimeRemainingPie product={product} />
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="body1" style={{ fontWeight: 500 }}>
              {product?.title || "Product Name"}
            </Typography>

            {showPricing()}
          </div>

          <div className={classes.imageContainer}>
            {!imageLoaded && <Skeleton className={classes.imageSkeleton} variant="rectangular" />}
            <img
              style={{ display: imageLoaded ? "block" : "none" }}
              className={classes.image}
              src={product && product.images.length > 0 && product.images[0].image}
              onLoad={() => setImageLoaded(true)}
              alt={product?.title}
            />
          </div>

          {soldout ? (
            <Typography className={classes.soldOutLabel} variant="body2">
              Sold out
            </Typography>
          ) : null}
        </StyledCard>
      </AnimateOnClick>
    </DelayedLink>
  );
};

export default ProductCard;
